import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { restoreCsrTheme } from '../../csr/csr-theme';
import { AgentStatus, SupervisorService, QueueItem } from '../../../core/services/supervisor.service';

interface QueueRow {
  queueId: number;
  name: string;   // callerNumber
  topic: string;
  ch: string;     // channelType
  wait: string;   // formatted wait
  pri: string;    // priority
}

@Component({
  selector: 'app-supervisor-queue-monitor',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './supervisor-queue-monitor.component.html',
  styleUrls: ['./supervisor-queue-monitor.component.scss']
})
export class SupervisorQueueMonitorComponent implements OnInit, OnDestroy {
  assignedIds: number[] = [];
  toastMsg: string | null = null;
  agentDialogFor: QueueRow | null = null;
  availableAgents: AgentStatus[] = [];
  loadingAgents = false;
  assigningAgentExt: string | null = null;
  assignError: string | null = null;

  private toastTimeout?: ReturnType<typeof setTimeout>;
  private pollInterval?: ReturnType<typeof setInterval>;
  private subs = new Subscription();

  stats: Array<{ label: string; val: string; col: string }> = [
    { label: 'Total Queued', val: '—', col: 'amber'  },
    { label: 'Voice',        val: '—', col: 'blue'   },
    { label: 'Avg Wait',     val: '—', col: 'red'    },
  ];

  queue: QueueRow[] = [];

  constructor(private supervisor: SupervisorService) {}

  ngOnInit(): void {
    restoreCsrTheme();

    this.supervisor.fetchQueue();
    this.supervisor.fetchStats();

    this.pollInterval = setInterval(() => {
      this.supervisor.fetchQueue();
    }, 10000);

    this.subs.add(this.supervisor.queue$.subscribe(items => {
      const waitingIds = new Set(items.map(item => item.queueId));
      this.assignedIds = this.assignedIds.filter(id => !waitingIds.has(id));
      this.queue = items.map(q => this.mapQueue(q));
      this.stats[0].val = String(items.length);
      this.stats[1].val = String(items.filter(q => q.channelType === 'voice').length);
      const avgWait = items.length
        ? Math.round(items.reduce((sum, q) => sum + q.waitSeconds, 0) / items.length)
        : 0;
      this.stats[2].val = this.fmtSecs(avgWait);
    }));
  }

  ngOnDestroy(): void {
    if (this.pollInterval) clearInterval(this.pollInterval);
    if (this.toastTimeout) clearTimeout(this.toastTimeout);
    this.subs.unsubscribe();
  }

  openAssignDialog(row: QueueRow): void {
    if (this.assignedIds.includes(row.queueId)) return;
    this.agentDialogFor = row;
    this.availableAgents = [];
    this.assignError = null;
    this.assigningAgentExt = null;
    this.loadingAgents = true;
    this.supervisor.getAvailableQueueAgents().subscribe({
      next: agents => {
        this.availableAgents = agents;
        this.loadingAgents = false;
        if (!agents.length) {
          this.assignError = 'No free agents are available right now.';
        }
      },
      error: () => {
        this.loadingAgents = false;
        this.assignError = 'Unable to load available agents.';
      }
    });
  }

  closeAssignDialog(): void {
    if (this.assigningAgentExt) return;
    this.agentDialogFor = null;
    this.availableAgents = [];
    this.assignError = null;
  }

  assignToAgent(agent: AgentStatus): void {
    if (!this.agentDialogFor || this.assigningAgentExt) return;
    const row = this.agentDialogFor;
    this.assignError = null;
    this.assigningAgentExt = agent.sipExtension;
    this.supervisor.assignQueueItem(row.queueId, agent.sipExtension).subscribe(result => {
      this.assigningAgentExt = null;
      if (result?.error) {
        this.assignError = result.message || 'Unable to assign this queue item.';
        this.supervisor.fetchQueue();
        this.refreshAgents();
        return;
      }
      this.assignedIds = [...this.assignedIds, row.queueId];
      this.showToast(`${row.name} is ringing ${agent.fullName} (${agent.sipExtension})`);
      this.closeAssignDialog();
      this.supervisor.fetchQueue();
    });
  }

  agentInitials(agent: AgentStatus): string {
    const name = agent.fullName || agent.sipExtension || '';
    const initials = name.trim().split(/\s+/).slice(0, 2).map(part => part[0]).join('');
    return (initials || agent.sipExtension.slice(-2)).toUpperCase();
  }

  waitColor(wait: string): string {
    const mins = parseInt(wait.split(':')[0] ?? '0', 10);
    return mins > 3 ? '#ef5350' : 'inherit';
  }

  priClass(pri: string): string { return `p-${pri.toLowerCase()}`; }

  chClass(ch: string): string { return `ch-${ch.toLowerCase()}`; }

  private mapQueue(q: QueueItem): QueueRow {
    return {
      queueId: q.queueId,
      name:    q.callerNumber,
      topic:   q.topic || q.channelType,
      ch:      q.channelType,
      wait:    this.fmtSecs(q.waitSeconds),
      pri:     q.priority,
    };
  }

  private fmtSecs(seconds: number): string {
    if (!seconds || seconds < 0) return '0:00';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  }

  private showToast(msg: string): void {
    this.toastMsg = msg;
    if (this.toastTimeout) clearTimeout(this.toastTimeout);
    this.toastTimeout = setTimeout(() => { this.toastMsg = null; }, 3000);
  }

  private refreshAgents(): void {
    this.loadingAgents = true;
    this.supervisor.getAvailableQueueAgents().subscribe(agents => {
      this.availableAgents = agents;
      this.loadingAgents = false;
      if (!agents.length && !this.assignError) {
        this.assignError = 'No free agents are available right now.';
      }
    });
  }
}
